<template>
  <div
    :id="id"
    :data-accordion="accordionType"
    :data-active-classes="activeClasses"
    :data-inactive-classes="inactiveClasses"
  >
    <div v-for="(item, index) in items" :key="index">
      <h2 :id="`${id}-heading-${index + 1}`">
        <button
          type="button"
          class="flex items-center justify-between w-full p-5 font-medium rtl:text-right text-gray-500 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-800 dark:border-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 gap-3"
          :class="{
            'border-b border-gray-200 dark:border-gray-700':
              accordionType === 'flush',
            'border border-gray-200': accordionType !== 'flush',
            'rounded-t-xl': accordionType !== 'flush' && index === 0,
            'rounded-b-xl':
              accordionType !== 'flush' &&
              index === items.length - 1 &&
              !expanded[index],
            'border-t-0': accordionType !== 'flush' && index !== 0,
            'bg-white dark:bg-gray-900 text-gray-900 dark:text-white':
              accordionType === 'open' && expanded[index],
            'text-gray-500 dark:text-gray-400':
              accordionType === 'open' && !expanded[index]
          }"
          :data-accordion-target="`#${id}-body-${index + 1}`"
          :aria-expanded="isExpanded(index)"
          :aria-controls="`${id}-body-${index + 1}`"
          @click="toggleAccordion(index)"
        >
          <slot :name="`content-${index}-header`" :item="item">
            <span class="flex items-center">
              <div
                v-if="svgString"
                class="w-5 h-5 me-2 shrink-0"
                v-html="svgString"
              />
              {{ item.title }}
            </span>
          </slot>
          <svg
            v-if="expanded[index]"
            data-accordion-icon
            class="w-3 h-3 shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5 5 1 1 5"
            />
          </svg>
          <svg
            v-else
            data-accordion-icon
            class="w-3 h-3 shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M1 1 5 5 9 1"
            />
          </svg>
        </button>
      </h2>
      <div
        :id="`${id}-body-${index + 1}`"
        :class="{ hidden: !expanded[index] }"
        :aria-labelledby="`${id}-heading-${index + 1}`"
      >
        <div
          class="p-5 dark:bg-gray-900"
          :class="{
            'border-b border-gray-200 dark:border-gray-700':
              accordionType === 'flush',
            'border border-t-0 border-gray-200 dark:border-gray-700':
              accordionType !== 'flush',
            'rounded-b-xl':
              accordionType !== 'flush' && index === items.length - 1
          }"
        >
          <slot :name="`content-${index}-body`" :item="item">
            <!-- Default Content -->
            <p class="mb-2 text-gray-500 dark:text-gray-400">
              {{ item.content }}
            </p>
            <p
              v-if="item.link"
              class="text-blue-600 dark:text-blue-500 hover:underline"
            >
              Check out this guide to learn how to
              <a :href="item.link">{{ item.linkText }}</a
              >.
            </p>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
interface Item {
  title?: string;
  description?: string;
  open?: boolean;
}
const emit = defineEmits(["expanded"]);
const props = withDefaults(
  defineProps<{
    id: string;
    accordionType: "collapse" | "open" | "flush";
    items: Item[];
    activeClasses?: string;
    inactiveClasses?: string;
    svgString?: string;
  }>(),
  {
    activeClasses: "bg-white dark:bg-gray-900 text-gray-900 dark:text-white",
    inactiveClasses: "text-gray-500 dark:text-gray-400",
    svgString: ""
  }
);
const expanded = ref<boolean[]>(
  props.items.map((item: Item) => Boolean(item.open))
);

const toggleAccordion = (index) => {
  if (props.accordionType === "collapse" || props.accordionType === "flush") {
    expanded.value = expanded.value.map((_, i) =>
      i === index ? !expanded.value[index] : false
    );
  } else if (props.accordionType === "open") {
    expanded.value[index] = !expanded.value[index];
  }
};

const isExpanded = (index) => expanded.value[index];

watch(expanded, (val, oldVal) => {
  if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
    emit("expanded", val);
  }
});
onMounted(() => {
  if (typeof window !== "undefined" && window.Flowbite) {
    window.Flowbite.init();
  }
});
</script>

<style scoped></style>
